/**
 * Job Cost Detail Report
 *
 * Four-level expandable hierarchy:
 *   Job -> Parent Cost Code -> Cost Code (Task) -> Cost Type
 *   with individual transaction lines at level 4.
 *
 * Data sources:
 *   1. GLENTRY  - accrual-book entries on Cost of Sales accounts.
 *      - APJ journal is EXCLUDED (replaced by APBILLITEM).
 *      - PYRJ journal entries can be tied to TIMESHEETENTRY for hours.
 *      - All other journals (ARJ, CDJ, CRJ, GJ, etc.) pass through as-is.
 *   2. APBILLITEM - replaces the APJ journal; links to APBILL for drill-through.
 *   3. TIMESHEETENTRY - optionally tied to PYRJ entries by matching
 *      EMPLOYEEID + PROJECTID + TASKID + COSTTYPEID and
 *      abs(GLENTRY.ENTRY_DATE - TS_ENDDATE) <= user-specified tolerance.
 *
 * Supporting lookups:
 *   - GLACCOUNT  - identifies Cost of Sales accounts (CATEGORY LIKE 'Cost%',
 *                   ACCOUNTTYPE = 'incomestatement')
 *   - GLBATCH    - JOURNAL identification + RECORD_URL for GL drill-through
 *   - APBILL     - RECORD_URL for AP Bill drill-through
 *   - TIMESHEET  - RECORD_URL for timesheet drill-through
 *   - TASK       - PARENTID + PARENTTASKNAME for parent cost code grouping
 *
 * Journals (fixed, consistent across clients):
 *   APJ ARJ CDJ CRJ EEDJ EEJ GJ IEJ IJ OBJ PYRJ PDCJ PLCJ PJ SFOJ SJ UNB WIPJ
 *
 * Amount logic:
 *   GLENTRY:    AMOUNT x TR_TYPE  (TR_TYPE=1 debit, -1 credit)
 *   APBILLITEM: AMOUNT            (always positive for costs)
 *
 * Timesheet dedup:  hours are assigned to only ONE GLENTRY per unique
 *   EMPLOYEEID|PROJECTID|TASKID|COSTTYPEID|ENTRY_DATE key to avoid
 *   double-counting when multiple accounts (labor + burden) share dimensions.
 *
 * Styling:  returns detailLevel=4 so reportXlsx.js can apply faint styling
 *   only to transaction rows (level 4), keeping summary rows (0-3) readable.
 */

import { parseDate } from './reportHelpers';

const jobCostDetail = {
  id: 'job-cost-detail',
  name: 'Job Cost Detail',
  description: 'Job costs by Parent Cost Code, Cost Code, and Cost Type with GL, AP Bill, and optional timesheet detail. Expandable rows drill into individual transactions with links to source documents.',
  category: 'Project Management',

  parameters: [
    {
      id: 'projectFilter', label: 'Project ID', type: 'text',
      default: '', description: 'Filter to a specific Project ID (leave blank for all)'
    },
    {
      id: 'enableTimesheetTying', label: 'Tie Timesheets to Payroll', type: 'checkbox',
      default: false, description: 'Match PYRJ journal entries to timesheets for hours display'
    },
    {
      id: 'timesheetDaysTolerance', label: 'Days Tolerance (PYRJ to Timesheet)', type: 'text',
      default: '7', description: 'Max days between GL Entry Date and Timesheet End Date'
    },
  ],

  queries: [
    // -- Cost of Sales account list --
    {
      id: 'accounts', object: 'GLACCOUNT',
      fields: ['ACCOUNTNO', 'TITLE', 'CATEGORY', 'ACCOUNTTYPE'],
      buildFilter: () => "CATEGORY LIKE 'Cost%' AND ACCOUNTTYPE = 'incomestatement' AND STATUS = 'active'"
    },
    // -- GL entries (all project-coded, posted) --
    {
      id: 'glEntries', object: 'GLENTRY',
      fields: [
        'RECORDNO', 'BATCHNO', 'ACCOUNTNO', 'TR_TYPE', 'AMOUNT',
        'DESCRIPTION', 'ENTRY_DATE',
        'PROJECTID', 'PROJECTNAME', 'TASKID', 'TASKNAME',
        'COSTTYPEID', 'COSTTYPENAME',
        'EMPLOYEEID', 'EMPLOYEENAME',
        'VENDORID', 'VENDORNAME',
        'CUSTOMERID', 'CUSTOMERNAME',
        'DEPARTMENT', 'LOCATION',
        'CURRENCY', 'TRX_AMOUNT',
        'RECORD_URL', 'STATE'
      ],
      buildFilter: (params) => {
        const parts = ["PROJECTID IS NOT NULL", "STATE = 'Posted'"];
        if (params.projectFilter?.trim()) {
          parts.push("PROJECTID = '" + params.projectFilter.trim() + "'");
        }
        return parts.join(' AND ');
      }
    },
    // -- GL Batches (journal type + RECORD_URL for two-hop) --
    {
      id: 'glBatches', object: 'GLBATCH',
      fields: [
        'RECORDNO', 'JOURNAL', 'BATCH_DATE', 'BATCH_TITLE',
        'RECORD_URL', 'MEGAENTITYID'
      ],
      buildFilter: () => "STATE = 'Posted'"
    },
    // -- AP Bill Items (replaces APJ journal) --
    {
      id: 'apBillItems', object: 'APBILLITEM',
      fields: [
        'RECORDNO', 'RECORDKEY', 'APBILL.RECORDNO', 'APBILL.RECORDID',
        'ACCOUNTNO', 'AMOUNT', 'ENTRYDESCRIPTION', 'ENTRY_DATE',
        'PROJECTID', 'PROJECTNAME', 'TASKID', 'TASKNAME',
        'COSTTYPEID', 'COSTTYPENAME',
        'VENDORID', 'VENDORNAME',
        'DEPARTMENTID', 'LOCATIONID',
        'STATE'
      ],
      buildFilter: (params) => {
        const parts = ["PROJECTID IS NOT NULL", "STATE NOT IN ('Reversed','Reversal','Voided','Deleted')"];
        if (params.projectFilter?.trim()) {
          parts.push("PROJECTID = '" + params.projectFilter.trim() + "'");
        }
        return parts.join(' AND ');
      }
    },
    // -- AP Bills (RECORD_URL for drill-through) --
    {
      id: 'apBills', object: 'APBILL',
      fields: ['RECORDNO', 'RECORDID', 'DOCNUMBER', 'RECORD_URL', 'MEGAENTITYID'],
      buildFilter: () => "STATE NOT IN ('Reversed','Reversal','Voided','Deleted')"
    },
    // -- Tasks (parent cost code lookup) --
    {
      id: 'tasks', object: 'TASK',
      fields: ['TASKID', 'NAME', 'PARENTID', 'PARENTTASKNAME', 'PROJECTID'],
      buildFilter: (params) => {
        if (params.projectFilter?.trim()) {
          return "PROJECTID = '" + params.projectFilter.trim() + "'";
        }
        return "RECORDNO > 0";
      }
    },
    // -- Timesheet entries (conditional on tying enabled) --
    {
      id: 'timesheetEntries', object: 'TIMESHEETENTRY',
      conditional: (params) => !!params.enableTimesheetTying,
      fields: [
        'RECORDNO', 'TIMESHEETKEY',
        'EMPLOYEEID', 'EMPLOYEENAME',
        'PROJECTID', 'TASKID', 'COSTTYPEID',
        'TIMETYPE', 'QTY',
        'TS_ENDDATE', 'ENTRYDATE',
        'RECORD_URL'
      ],
      buildFilter: (params) => {
        const parts = ["PROJECTID IS NOT NULL"];
        if (params.projectFilter?.trim()) {
          parts.push("PROJECTID = '" + params.projectFilter.trim() + "'");
        }
        return parts.join(' AND ');
      }
    },
    // -- Timesheets (RECORD_URL for drill-through) --
    {
      id: 'timesheets', object: 'TIMESHEET',
      conditional: (params) => !!params.enableTimesheetTying,
      fields: ['RECORDNO', 'RECORD_URL', 'MEGAENTITYID'],
      buildFilter: () => "RECORDNO > 0"
    },
  ],

  additionalQueries: [],
  optionalColumns: [],

  process: (datasets, params) => {
    const accounts = datasets.accounts || [];
    const glEntries = datasets.glEntries || [];
    const glBatches = datasets.glBatches || [];
    const apBillItems = datasets.apBillItems || [];
    const apBills = datasets.apBills || [];
    const taskRecords = datasets.tasks || [];
    const tsEntries = datasets.timesheetEntries || [];
    const timesheets = datasets.timesheets || [];

    const tolerance = parseInt(params.timesheetDaysTolerance) || 7;
    const tsEnabled = !!params.enableTimesheetTying;

    // =============================================
    // 1. Build Cost of Sales account set
    // =============================================
    const cosAccounts = new Set();
    const accountTitles = {};
    for (const acct of accounts) {
      const no = String(acct.ACCOUNTNO || '').trim();
      if (no) {
        cosAccounts.add(no);
        accountTitles[no] = acct.TITLE || '';
      }
    }

    // =============================================
    // 2. Build GLBATCH lookup  (RECORDNO -> batch info)
    // =============================================
    const batchLookup = {};
    for (const b of glBatches) {
      const rn = String(b.RECORDNO || '').trim();
      if (rn) {
        batchLookup[rn] = {
          journal: String(b.JOURNAL || '').trim(),
          batchDate: b.BATCH_DATE || '',
          batchTitle: b.BATCH_TITLE || '',
          url: b.RECORD_URL || '',
          entity: b.MEGAENTITYID || '',
        };
      }
    }

    // =============================================
    // 3. Build APBILL lookup  (RECORDNO -> bill info)
    // =============================================
    const billLookup = {};
    for (const bill of apBills) {
      const rn = String(bill.RECORDNO || '').trim();
      if (rn) {
        billLookup[rn] = {
          recordId: bill.RECORDID || '',
          docNumber: bill.DOCNUMBER || '',
          url: bill.RECORD_URL || '',
          entity: bill.MEGAENTITYID || '',
        };
      }
    }

    // =============================================
    // 4. Build TIMESHEET lookup  (RECORDNO -> url)
    // =============================================
    const tsLookup = {};
    for (const ts of timesheets) {
      const rn = String(ts.RECORDNO || '').trim();
      if (rn) {
        tsLookup[rn] = {
          url: ts.RECORD_URL || '',
          entity: ts.MEGAENTITYID || '',
        };
      }
    }

    // =============================================
    // 5. Build TASK parent lookup
    //    Key: "PROJECTID|TASKID" -> { parentId, parentName }
    // =============================================
    const taskParentLookup = {};
    for (const t of taskRecords) {
      const projId = String(t.PROJECTID || '').trim();
      const taskId = String(t.TASKID || '').trim();
      if (projId && taskId) {
        taskParentLookup[projId + '|' + taskId] = {
          parentId: String(t.PARENTID || '').trim(),
          parentName: t.PARENTTASKNAME || '',
        };
      }
    }

    // =============================================
    // 6. Build timesheet index for matching
    //    Key: EMPLOYEEID|PROJECTID|TASKID|COSTTYPEID
    //    Value: array of { tsEndDate, qty, timeType, timesheetKey }
    // =============================================
    const tsIndex = {};
    if (tsEnabled) {
      for (const tse of tsEntries) {
        const key = [
          String(tse.EMPLOYEEID || '').trim(),
          String(tse.PROJECTID || '').trim(),
          String(tse.TASKID || '').trim(),
          String(tse.COSTTYPEID || '').trim(),
        ].join('|');

        if (!tsIndex[key]) tsIndex[key] = [];
        tsIndex[key].push({
          tsEndDate: parseDate(tse.TS_ENDDATE),
          qty: parseFloat(tse.QTY) || 0,
          timeType: String(tse.TIMETYPE || '').trim().toUpperCase(),
          timesheetKey: String(tse.TIMESHEETKEY || '').trim(),
        });
      }
    }

    // =============================================
    // 7. Process GLENTRY -> transaction objects
    // =============================================
    // hierarchy: transactions[projectId].parents[parentId].tasks[taskId].costTypes[ctId].items[]
    const transactions = {};
    let skippedNonCos = 0, skippedApj = 0;
    const pyrjMatchConsumed = new Set();

    function ensureBucket(projId, projName, parentId, parentName, taskId, taskName, ctId, ctName) {
      if (!transactions[projId]) transactions[projId] = { name: projName, parents: {} };
      const proj = transactions[projId];
      if (!proj.parents[parentId]) proj.parents[parentId] = { name: parentName, tasks: {} };
      const parent = proj.parents[parentId];
      if (!parent.tasks[taskId]) parent.tasks[taskId] = { name: taskName, costTypes: {} };
      const task = parent.tasks[taskId];
      if (!task.costTypes[ctId]) task.costTypes[ctId] = { name: ctName, items: [] };
      return task.costTypes[ctId].items;
    }

    function resolveParent(projId, taskId) {
      const info = taskParentLookup[projId + '|' + taskId];
      if (info && info.parentId) return info;
      return { parentId: 'No Parent', parentName: 'No Parent' };
    }

    for (const gl of glEntries) {
      const acctNo = String(gl.ACCOUNTNO || '').trim();
      if (!cosAccounts.has(acctNo)) { skippedNonCos++; continue; }

      const batchNo = String(gl.BATCHNO || '').trim();
      const batch = batchLookup[batchNo] || {};
      const journal = batch.journal || '';

      // Skip APJ -- replaced by APBILLITEM
      if (journal === 'APJ') { skippedApj++; continue; }

      const trType = parseInt(gl.TR_TYPE) || 1;
      const amount = Math.round((parseFloat(gl.AMOUNT) || 0) * trType * 100) / 100;

      const projId = String(gl.PROJECTID || '').trim() || 'Unassigned';
      const projName = gl.PROJECTNAME || projId;
      const taskId = String(gl.TASKID || '').trim() || 'Unassigned';
      const taskName = gl.TASKNAME || taskId;
      const ctId = String(gl.COSTTYPEID || '').trim() || 'Unassigned';
      const ctName = gl.COSTTYPENAME || ctId;

      const parentInfo = resolveParent(projId, taskId);

      // -- Timesheet matching for PYRJ entries --
      let regHours = 0, otHours = 0, hoursUrl = '', hoursEntity = '';
      if (tsEnabled && journal === 'PYRJ') {
        const matchKey = [
          String(gl.EMPLOYEEID || '').trim(),
          projId, taskId, ctId,
        ].join('|');

        const entryDate = parseDate(gl.ENTRY_DATE);
        const dedupKey = matchKey + '|' + (gl.ENTRY_DATE || '');
        const alreadyConsumed = pyrjMatchConsumed.has(dedupKey);

        if (!alreadyConsumed && tsIndex[matchKey]) {
          let matchedTimesheetKey = '';
          for (const tse of tsIndex[matchKey]) {
            if (!entryDate || !tse.tsEndDate) continue;
            const diffDays = Math.abs(
              Math.floor((entryDate - tse.tsEndDate) / (1000 * 60 * 60 * 24))
            );
            if (diffDays <= tolerance) {
              if (tse.timeType === 'REG') regHours += tse.qty;
              else if (tse.timeType === 'OT') otHours += tse.qty;
              if (!matchedTimesheetKey && tse.timesheetKey) {
                matchedTimesheetKey = tse.timesheetKey;
              }
            }
          }
          if (regHours > 0 || otHours > 0) {
            pyrjMatchConsumed.add(dedupKey);
            const tsInfo = tsLookup[matchedTimesheetKey] || {};
            hoursUrl = tsInfo.url || '';
            hoursEntity = tsInfo.entity || '';
          }
        }
      }

      let nameDisplay = '';
      if (gl.EMPLOYEENAME) nameDisplay = gl.EMPLOYEENAME;
      else if (gl.VENDORNAME) nameDisplay = gl.VENDORNAME;
      else if (gl.CUSTOMERNAME) nameDisplay = gl.CUSTOMERNAME;

      const items = ensureBucket(projId, projName, parentInfo.parentId, parentInfo.parentName, taskId, taskName, ctId, ctName);
      items.push({
        date: gl.ENTRY_DATE || '',
        source: journal === 'PYRJ' ? 'Payroll' : 'GL',
        journal: journal,
        reference: batch.batchTitle || '',
        description: gl.DESCRIPTION || '',
        vendorEmployee: nameDisplay,
        account: acctNo,
        accountTitle: accountTitles[acctNo] || '',
        amount: amount,
        regHours: regHours,
        otHours: otHours,
        timesheetRef: (regHours > 0 || otHours > 0) ? 'Timesheet' : '',
        _refUrl: batch.url || '',
        _refEntity: batch.entity || '',
        _tsUrl: hoursUrl,
        _tsEntity: hoursEntity,
      });
    }

    // =============================================
    // 8. Process APBILLITEM -> transaction objects
    // =============================================
    let skippedApNonCos = 0;

    for (const line of apBillItems) {
      const acctNo = String(line.ACCOUNTNO || '').trim();
      if (!cosAccounts.has(acctNo)) { skippedApNonCos++; continue; }

      const amount = Math.round((parseFloat(line.AMOUNT) || 0) * 100) / 100;

      const projId = String(line.PROJECTID || '').trim() || 'Unassigned';
      const projName = line.PROJECTNAME || projId;
      const taskId = String(line.TASKID || '').trim() || 'Unassigned';
      const taskName = line.TASKNAME || taskId;
      const ctId = String(line.COSTTYPEID || '').trim() || 'Unassigned';
      const ctName = line.COSTTYPENAME || ctId;

      const parentInfo = resolveParent(projId, taskId);

      const billRn = String(line.RECORDKEY || line['APBILL.RECORDNO'] || '').trim();
      const billInfo = billLookup[billRn] || {};
      const billId = line['APBILL.RECORDID'] || billInfo.recordId || '';

      const items = ensureBucket(projId, projName, parentInfo.parentId, parentInfo.parentName, taskId, taskName, ctId, ctName);
      items.push({
        date: line.ENTRY_DATE || '',
        source: 'AP Bill',
        journal: 'APJ',
        reference: billId,
        description: line.ENTRYDESCRIPTION || '',
        vendorEmployee: line.VENDORNAME || '',
        account: acctNo,
        accountTitle: accountTitles[acctNo] || '',
        amount: amount,
        regHours: 0,
        otHours: 0,
        timesheetRef: '',
        _refUrl: billInfo.url || '',
        _refEntity: billInfo.entity || '',
        _tsUrl: '',
        _tsEntity: '',
      });
    }

    // =============================================
    // 9. Flatten hierarchy into rows with outline levels
    //    Level 0 = Project
    //    Level 1 = Parent Cost Code
    //    Level 2 = Cost Code
    //    Level 3 = Cost Type
    //    Level 4 = Transaction line (detail)
    // =============================================
    const data = [];
    const rowOutlineLevels = [];
    const detailRowIndices = [];

    let grandAmount = 0, grandReg = 0, grandOt = 0;

    const emptyRow = {
      'Project ID': '', 'Project Name': '',
      'Parent Cost Code': '', 'Parent Cost Code Name': '',
      'Cost Code': '', 'Cost Code Name': '',
      'Cost Type': '', 'Cost Type Name': '',
      'Date': '', 'Source': '', 'Journal': '', 'Reference': '',
      'Description': '', 'Vendor / Employee': '',
      'Account': '', 'Account Title': '',
      'Amount': 0, 'REG Hours': '', 'OT Hours': '', 'Timesheet': '',
    };

    const sortedProjects = Object.keys(transactions).sort();

    for (const projId of sortedProjects) {
      const proj = transactions[projId];
      let projAmount = 0, projReg = 0, projOt = 0;

      const projRowIdx = data.length;
      data.push(null);
      rowOutlineLevels.push(0);

      const sortedParents = Object.keys(proj.parents).sort();

      for (const parentId of sortedParents) {
        const parent = proj.parents[parentId];
        let parentAmount = 0, parentReg = 0, parentOt = 0;

        const parentRowIdx = data.length;
        data.push(null);
        rowOutlineLevels.push(1);
        detailRowIndices.push(parentRowIdx);

        const sortedTasks = Object.keys(parent.tasks).sort();

        for (const taskId of sortedTasks) {
          const task = parent.tasks[taskId];
          let taskAmount = 0, taskReg = 0, taskOt = 0;

          const taskRowIdx = data.length;
          data.push(null);
          rowOutlineLevels.push(2);
          detailRowIndices.push(taskRowIdx);

          const sortedCts = Object.keys(task.costTypes).sort();

          for (const ctId of sortedCts) {
            const ct = task.costTypes[ctId];
            let ctAmount = 0, ctReg = 0, ctOt = 0;

            const ctRowIdx = data.length;
            data.push(null);
            rowOutlineLevels.push(3);
            detailRowIndices.push(ctRowIdx);

            const sortedItems = ct.items.sort((a, b) => {
              return String(a.date || '').localeCompare(String(b.date || ''));
            });

            for (const txn of sortedItems) {
              ctAmount += txn.amount;
              ctReg += txn.regHours;
              ctOt += txn.otHours;

              detailRowIndices.push(data.length);
              data.push(Object.assign({}, emptyRow, {
                'Date': txn.date,
                'Source': txn.source,
                'Journal': txn.journal,
                'Reference': txn.reference,
                'Description': txn.description,
                'Vendor / Employee': txn.vendorEmployee,
                'Account': txn.account,
                'Account Title': txn.accountTitle,
                'Amount': txn.amount,
                'REG Hours': txn.regHours || '',
                'OT Hours': txn.otHours || '',
                'Timesheet': txn.timesheetRef,
                '_refUrl': txn._refUrl,
                '_refEntity': txn._refEntity,
                '_tsUrl': txn._tsUrl,
                '_tsEntity': txn._tsEntity,
              }));
              rowOutlineLevels.push(4);
            }

            // Cost Type summary
            ctAmount = Math.round(ctAmount * 100) / 100;
            data[ctRowIdx] = Object.assign({}, emptyRow, {
              'Cost Type': ctId,
              'Cost Type Name': ct.name,
              'Amount': ctAmount,
              'REG Hours': ctReg || '',
              'OT Hours': ctOt || '',
            });

            taskAmount += ctAmount;
            taskReg += ctReg;
            taskOt += ctOt;
          }

          // Cost Code summary
          taskAmount = Math.round(taskAmount * 100) / 100;
          data[taskRowIdx] = Object.assign({}, emptyRow, {
            'Cost Code': taskId,
            'Cost Code Name': task.name,
            'Amount': taskAmount,
            'REG Hours': taskReg || '',
            'OT Hours': taskOt || '',
          });

          parentAmount += taskAmount;
          parentReg += taskReg;
          parentOt += taskOt;
        }

        // Parent Cost Code summary
        parentAmount = Math.round(parentAmount * 100) / 100;
        data[parentRowIdx] = Object.assign({}, emptyRow, {
          'Parent Cost Code': parentId,
          'Parent Cost Code Name': parent.name,
          'Amount': parentAmount,
          'REG Hours': parentReg || '',
          'OT Hours': parentOt || '',
        });

        projAmount += parentAmount;
        projReg += parentReg;
        projOt += parentOt;
      }

      // Project summary
      projAmount = Math.round(projAmount * 100) / 100;
      data[projRowIdx] = Object.assign({}, emptyRow, {
        'Project ID': projId,
        'Project Name': proj.name,
        'Amount': projAmount,
        'REG Hours': projReg || '',
        'OT Hours': projOt || '',
      });

      grandAmount += projAmount;
      grandReg += projReg;
      grandOt += projOt;
    }

    // =============================================
    // 10. Define columns + grouped columns
    // =============================================
    const columns = [
      'Project ID', 'Project Name',
      'Parent Cost Code', 'Parent Cost Code Name',
      'Cost Code', 'Cost Code Name',
      'Cost Type', 'Cost Type Name',
      'Date', 'Source', 'Journal', 'Reference',
      'Description', 'Vendor / Employee',
      'Account', 'Account Title',
      'Amount', 'REG Hours', 'OT Hours', 'Timesheet'
    ];

    // Group transaction-detail columns (collapsed by default)
    const detailColStart = columns.indexOf('Date');
    const detailColEnd = columns.indexOf('Account Title');
    const detailIndices = [];
    for (let i = detailColStart; i <= detailColEnd; i++) detailIndices.push(i);
    const groupedColumns = [{ indices: detailIndices }];

    // =============================================
    // 11. Summary
    // =============================================
    const projectCount = sortedProjects.length;
    const txnCount = data.filter(function(_, i) { return rowOutlineLevels[i] === 4; }).length;

    const summaryLines = [
      projectCount + ' project(s)',
      txnCount + ' transaction(s)',
      '',
      'Total Cost: $' + grandAmount.toLocaleString('en-US', { minimumFractionDigits: 2 }),
    ];
    if (grandReg > 0 || grandOt > 0) {
      summaryLines.push(
        'Total REG Hours: ' + grandReg.toLocaleString('en-US', { minimumFractionDigits: 2 }),
        'Total OT Hours: ' + grandOt.toLocaleString('en-US', { minimumFractionDigits: 2 })
      );
    }
    if (skippedApj > 0) summaryLines.push('', '(' + skippedApj + ' APJ entries replaced by AP Bill items)');
    if (skippedNonCos > 0) summaryLines.push('(' + skippedNonCos + ' non-CoS GL entries excluded)');
    if (skippedApNonCos > 0) summaryLines.push('(' + skippedApNonCos + ' non-CoS AP Bill items excluded)');

    return {
      columns: columns,
      data: data,
      detailRowIndices: detailRowIndices,
      rowOutlineLevels: rowOutlineLevels,
      groupedColumns: groupedColumns,
      detailLevel: 4,
      formatRules: {
        'Amount': { type: 'currency', decimals: 2 },
        'REG Hours': { type: 'number', decimals: 2 },
        'OT Hours': { type: 'number', decimals: 2 },
        'Reference': { type: 'hyperlink', urlKey: '_refUrl', entityKey: '_refEntity' },
        'Timesheet': { type: 'hyperlink', urlKey: '_tsUrl', entityKey: '_tsEntity' },
      },
      summary: { lines: summaryLines },
    };
  },

  formatRules: {
    'Amount': { type: 'currency', decimals: 2 },
    'REG Hours': { type: 'number', decimals: 2 },
    'OT Hours': { type: 'number', decimals: 2 },
    'Reference': { type: 'hyperlink', urlKey: '_refUrl', entityKey: '_refEntity' },
    'Timesheet': { type: 'hyperlink', urlKey: '_tsUrl', entityKey: '_tsEntity' },
  }
};

export default jobCostDetail;